import React from 'react';
import { levelFromStatus } from '../lib/levels';

// Tabuľka dokladov na dashboarde. Stavový štítok hovorí aj U KOHO doklad leží
// a ako dlho (waiting_on / waiting_since z GET /documents) — používatelia
// nevedeli, prečo doklad po schválení L1 ešte nie je u účtovníčky.
export interface TableDoc {
  id: string;
  vendor: string;
  original_filename: string;
  invoice_number?: string | null;
  amount: number;
  total_with_vat?: number | string | null;
  currency: string;
  status: string;
  due_date: string | null;
  is_paid: boolean;
  created_at: string;
  category?: string;
  submitted_by_email?: string;
  auto_approved?: boolean;
  accountant_handled_at?: string | null;
  dispatched_at?: string | null;
  ocr_warning?: string | null;
  duplicate_of_id?: string | null;
  duplicate_reason?: string | null;
  waiting_level?: number | null;
  waiting_on?: string[];
  waiting_since?: string | null;
}

interface Props<T extends TableDoc> {
  docs: T[];
  onOpen: (d: T) => void;
  selectable: (d: T) => boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  showCheckboxes: boolean;
}

const SYMBOL: Record<string, string> = { EUR: '€', USD: '$', CZK: 'Kč', GBP: '£', PLN: 'zł', HUF: 'Ft' };
const money = (v: any, cur: string) => {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || !Number.isFinite(n)) return null;
  return `${n.toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${SYMBOL[cur] || cur || ''}`.trim();
};
const day = (s: string) => new Date(s).toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric', year: 'numeric' });
const dayShort = (s: string) => new Date(s).toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric' });

const daysBetween = (from: string) => {
  const a = new Date(from); a.setHours(0, 0, 0, 0);
  const b = new Date(); b.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000));
};
const daysLabel = (n: number) => n === 0 ? 'dnes' : n === 1 ? '1 deň' : n < 5 ? `${n} dni` : `${n} dní`;
const daysUntil = (due: string) => {
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(due); b.setHours(0, 0, 0, 0);
  return Math.ceil((b.getTime() - a.getTime()) / 86400000);
};
// "Nikola Kormaňáková" → "Nikola K."
const shortName = (full: string) => {
  const parts = full.trim().split(/\s+/);
  return parts.length < 2 ? full : `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

export function statusInfo(d: TableDoc): { cls: string; label: string; sub: string | null } {
  const lvl = levelFromStatus(d.status);
  if (lvl !== null) {
    const who = (d.waiting_on || []).map(shortName).join(', ');
    const since = d.waiting_since ? daysLabel(daysBetween(d.waiting_since)) : null;
    return { cls: 'is-wait', label: `Čaká na L${lvl}`, sub: [who, since].filter(Boolean).join(' · ') || null };
  }
  switch (d.status) {
    case 'approved':
      return {
        cls: 'is-ok', label: 'Schválené',
        sub: d.dispatched_at ? `u účtovníčky od ${dayShort(d.dispatched_at)}` : 'čaká na odoslanie účtovníčke',
      };
    case 'rejected':
      return { cls: 'is-bad', label: 'Zamietnuté', sub: d.accountant_handled_at ? `vybavené ${dayShort(d.accountant_handled_at)}` : null };
    case 'returned':
      return { cls: 'is-review', label: 'Vrátené na opravu', sub: null };
    default:
      return { cls: 'is-muted', label: d.status, sub: null };
  }
}

export default function DocumentsTable<T extends TableDoc>({ docs, onOpen, selectable, selectedIds, onToggle, showCheckboxes }: Props<T>) {
  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            {showCheckboxes && <th className="col-check" />}
            <th>Dodávateľ</th>
            <th>Suma</th>
            <th>Splatnosť</th>
            <th>Stav</th>
          </tr>
        </thead>
        <tbody>
          {docs.map(d => {
            const st = statusInfo(d);
            const total = money(d.total_with_vat, d.currency) || money(d.amount, d.currency);
            const net = money(d.amount, d.currency);
            const open = levelFromStatus(d.status) !== null;
            const left = d.due_date ? daysUntil(d.due_date) : null;
            const late = !d.is_paid && open && left !== null && left < 0;
            const soon = !d.is_paid && open && left !== null && left >= 0 && left <= 2;
            return (
              <tr key={d.id} onClick={() => onOpen(d)}>
                {showCheckboxes && (
                  <td className="col-check" onClick={e => e.stopPropagation()}>
                    {selectable(d) && (
                      <input type="checkbox" checked={selectedIds.includes(d.id)} onChange={() => onToggle(d.id)}
                        title="Vybrať na hromadnú akciu" style={{ width: 17, height: 17, cursor: 'pointer' }} />
                    )}
                  </td>
                )}
                <td className="col-vendor">
                  <span className="dash-vendor">{d.vendor || d.original_filename || 'Neznámy dokument'}</span>
                  <span className="dash-sub">
                    {[d.invoice_number ? `č. ${d.invoice_number}` : null, d.category || null, `prijaté ${dayShort(d.created_at)}`].filter(Boolean).join(' · ')}
                  </span>
                </td>
                <td className="col-sum">
                  {total || <span style={{ color: '#9ca3af' }}>nezistená</span>}
                  {total && net && total !== net && <span className="dash-sub">bez DPH {net}</span>}
                </td>
                <td className="col-due">
                  {d.due_date ? (
                    <span className={late ? 'dash-due-late' : undefined}>
                      {day(d.due_date)}
                      {late && <span className="dash-sub" style={{ color: '#b91c1c' }}>po splatnosti</span>}
                      {soon && <span className="dash-sub" style={{ color: '#b4530a' }}>{left === 0 ? 'dnes' : left === 1 ? 'zajtra' : 'o 2 dni'}</span>}
                    </span>
                  ) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td className="col-status">
                  <span className={`dash-badge ${st.cls}`}>{st.label}</span>
                  {st.sub && <span className="dash-sub">{st.sub}</span>}
                  <div className="dash-flags">
                    {d.ocr_warning && <span className="dash-flag" title={d.ocr_warning} style={{ background: '#fef3c7', color: '#b45309' }}>⚠ OCR rozpor</span>}
                    {d.duplicate_of_id && <span className="dash-flag" title={d.duplicate_reason || 'Tento doklad už v systéme pravdepodobne je'} style={{ background: '#fee2e2', color: '#b91c1c' }}>⚠ Duplikát?</span>}
                    {d.is_paid && <span className="dash-flag" style={{ background: '#dcfce7', color: '#16a34a' }}>✓ Uhradené</span>}
                    {d.submitted_by_email === 'peppol' && <span className="dash-flag" title="Elektronická faktúra prijatá cez Peppol" style={{ background: '#ede9fe', color: '#6d28d9' }}>⚡ e-faktúra</span>}
                    {d.auto_approved && <span className="dash-flag" style={{ background: '#dbeafe', color: '#1e40af' }}>⚡ Auto</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
